import { Link } from "react-router-dom";

const UPDATED = "July 13, 2026";

const POLICIES = {
  privacy: {
    eyebrow: "Privacy Policy",
    title: "Your information helps us serve you properly.",
    intro:
      "Note Console collects only the information we need to process your order, deliver your planner, support your account, and improve your shopping experience.",
    sections: [
      {
        title: "What We Collect",
        body: [
          "We may collect your name, phone number, email address, shipping address, billing details, order details, and payment status.",
          "Payment information is handled through secure payment partners. We do not store full card, UPI, net banking, or wallet credentials on our website.",
        ],
      },
      {
        title: "Why We Collect It",
        body: [
          "We use your details to confirm purchases, collect payment, pack and ship products, share delivery updates, provide customer support, send invoices, and resolve order issues.",
          "Your phone number and address are required because physical planners must be delivered to the correct person and location.",
        ],
      },
      {
        title: "How We Protect It",
        body: [
          "We keep customer information limited to service-related use and do not sell your personal information.",
          "We may share only the details required with delivery partners, payment providers, or support tools so they can complete the service you requested.",
        ],
      },
      {
        title: "Your Choices",
        body: [
          "You can contact us to correct your details, ask about your data, or request removal where legally and operationally possible.",
          "Some order information may need to be retained for invoices, fraud prevention, legal compliance, and customer support history.",
        ],
      },
    ],
  },
  terms: {
    eyebrow: "Terms of Service",
    title: "Clear terms for shopping with Note Console.",
    intro:
      "By using our website or placing an order, you agree to these terms. Please read them before purchasing.",
    sections: [
      {
        title: "Orders and Information",
        body: [
          "You agree to provide accurate name, contact, shipping, and payment details while placing an order.",
          "Incorrect or incomplete details may delay delivery, prevent successful shipment, or require additional verification.",
        ],
      },
      {
        title: "Products and Availability",
        body: [
          "We aim to show product details, pricing, and availability accurately, but minor differences in color, finish, or display may occur because screens and production batches can vary.",
          "We may update, pause, or discontinue products, offers, or prices when needed.",
        ],
      },
      {
        title: "Payment",
        body: [
          "Orders are confirmed only after payment is completed or an accepted payment method is selected.",
          "If a payment fails, is reversed, or appears suspicious, we may hold or cancel the order until the issue is resolved.",
        ],
      },
      {
        title: "Use of the Website",
        body: [
          "You may not misuse the website, attempt unauthorized access, copy our content for commercial use, or place fraudulent orders.",
          "We may refuse service, cancel suspicious orders, or limit access when required to protect the store and our customers.",
        ],
      },
    ],
  },
  shipping: {
    eyebrow: "Shipping & Issue Support",
    title: "Delivery timelines and issue support.",
    intro:
      "We pack each planner carefully and ship based on your delivery location and courier availability.",
    sections: [
      {
        title: "Shipping Timeline",
        body: [
          "Shipping time depends on the delivery location and courier route.",
          "Most orders may take 7 to 12 days from the shipping date to reach you. Remote locations, weather, holidays, or courier delays may take longer.",
        ],
      },
      {
        title: "Delivery Details",
        body: [
          "Please provide a correct phone number, email address, full name, and complete address so the courier can contact you and deliver successfully.",
          "If the address is incomplete or the customer is unavailable, delivery may be delayed or returned by the courier.",
        ],
      },
      {
        title: "Issue-Based Returns",
        body: [
          "We accept returns only when a wrong product is delivered or an item is missing from your order.",
          "We cannot accept returns for change of mind, preference, or after-use concerns.",
        ],
      },
      {
        title: "How to Report an Issue",
        body: [
          "If you receive a wrong or incomplete order, contact us within 48 hours of delivery with your order number, photos of the package, and photos of the product received.",
          "After verification, we will help with the correct replacement, missing item shipment, or another suitable resolution.",
        ],
      },
    ],
  },
};

export default function PolicyPage({ type }) {
  const policy = POLICIES[type] || POLICIES.privacy;

  return (
    <div data-testid={`${type}-page`}>
      <section className="bg-ivory border-b border-line">
        <div className="container-nc py-16 md:py-20 max-w-4xl">
          <p className="overline mb-4">{policy.eyebrow}</p>
          <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-tight">
            {policy.title}
          </h1>
          <p className="mt-5 text-muted_brown max-w-2xl leading-relaxed">
            {policy.intro}
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.16em] text-muted_brown">
            Last updated: {UPDATED}
          </p>
        </div>
      </section>

      <section className="container-nc py-16 md:py-20 max-w-4xl">
        <div className="space-y-10">
          {policy.sections.map((section) => (
            <article key={section.title} className="border-b border-line pb-8 last:border-b-0">
              <h2 className="font-serif text-3xl text-espresso mb-4">{section.title}</h2>
              <div className="space-y-3 text-muted_brown leading-relaxed">
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 bg-cream border border-line p-6 rounded-xl">
          <h3 className="font-serif text-2xl text-espresso mb-2">Need help?</h3>
          <p className="text-sm text-muted_brown mb-5">
            For order, shipping, privacy, or missing/wrong product questions, contact the Note Console team.
          </p>
          <Link to="/help" className="btn-primary">
            Contact Support
          </Link>
        </div>
      </section>
    </div>
  );
}
